import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTrigger } from '../../../../../components/ui/dialog'
import { useExplorer } from '../../../providers'
import { useExplorerCss } from '../../../hooks/useExplorerCss'
import NumberControl from '../NumberControl'
import FieldClear from './FieldClear'
import { useSbpPopover } from './useSbpPopover'
import { useSbpCompact, SrLabel } from './compact'

/**
 * Promoted BEDROOMS bar segment (#922, toolkit #461 `bar_promoted_filter`).
 * Renders only when `searchbar.barPromotedFilter === 'bedrooms'`, between Who
 * and the Filters field. The control SHARES the applied bedrooms filter with
 * the filter-pane stepper (module: same hidden input), so the URL contract
 * and the pane's badge count are unchanged — an apply here is exactly a pane
 * apply that only touches bedrooms. The popover reuses the guests-popover
 * shell (module css:368-372: same positioning/radius/shadow, one stepper row,
 * shrunk to content). Desktop bar only; the mobile sheet keeps its stage flow.
 */
export default function BedroomsField({ advanceSignal = 0, onApplyAdvance }: { advanceSignal?: number; onApplyAdvance?: () => void } = {}) {
  const { state, setFilter } = useExplorer()
  const cssString = useExplorerCss()
  const [open, setOpen] = useState(false)
  const { triggerRef, measureNow, anchored, wrapperStyle } = useSbpPopover(true, 'right', open)
  const compactBar = useSbpCompact()

  // Chained popovers (#923): guests Done hands the hero bar here when the
  // promoted segment is present (module searchbar-pro.js:2832).
  useEffect(() => {
    if (!advanceSignal) return
    measureNow()
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSignal])
  const [bedrooms, setBedrooms] = useState(state.visitorFilters.bedrooms ?? 0)

  // Re-seed the draft from the APPLIED filters on every open (GuestFilter
  // parity): the filter pane's stepper writes the same key, so a mount-time
  // draft would go stale after a pane apply.
  useEffect(() => {
    if (!open) return
    setBedrooms(state.visitorFilters.bedrooms ?? 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const appliedBedrooms = state.visitorFilters.bedrooms ?? 0
  const applied = appliedBedrooms > 0

  // Live commit (r7 #936): this segment is always a desktop popover, and the
  // module's steppers write state directly with the popover open — no staged
  // draft anywhere (searchbar-pro.js:2735-2754 pattern).
  const changeBedrooms = (v: number) => {
    setBedrooms(v)
    setFilter({ bedrooms: v > 0 ? v : undefined })
  }

  const handleApply = () => {
    // Close-only "Done" now — the value is already committed per tap; the
    // idempotent re-commit guards a race with a sibling writer of the key.
    setFilter({ bedrooms: bedrooms > 0 ? bedrooms : undefined })
    setOpen(false)
    // Chain hand-off (#923): bedrooms sits between Who and the Filters field,
    // so Done advances to the Filters field under chained mode
    // (module searchbar-pro.js:2852-2864) — hero-gated in advanceFrom (r7).
    onApplyAdvance?.()
  }

  const handleClear = () => {
    setBedrooms(0)
    setFilter({ bedrooms: undefined })
  }

  return (
    // Always a desktop-bar popover, so always non-modal — one-click field
    // switching (#923); see the DateFilter pro branch for the full rationale.
    <Dialog modal={false} open={open} onOpenChange={(o) => { if (o) measureNow(); setOpen(o) }}>
      <DialogTrigger asChild>
        <button
          type="button"
          ref={triggerRef}
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-bedrooms-field${applied ? ' hfse-sbp-field-has-value' : ''}`}
        >
          {compactBar.compact ? <SrLabel>Bedrooms</SrLabel> : <span className="hfse-sbp-field-label">Bedrooms</span>}
          <span className={`hfse-sbp-field-value ${applied ? '' : 'hfse-sbp-field-placeholder'}`}>
            {applied ? `${appliedBedrooms}+` : 'Any'}
          </span>
          {applied && <FieldClear label="Clear bedrooms" onClear={handleClear} />}
        </button>
      </DialogTrigger>
      <DialogContent
        className={`hfse-form-modal hfse-form-guests-options hfse-sbp-popover${anchored ? ' hfse-sbp-popover-anchored' : ''}`}
        overlayClassName={`hfse-sbp-popover-overlay${anchored ? ' hfse-sbp-popover-overlay-anchored' : ''}`}
        wrapperStyle={wrapperStyle}
        elevateDom
        cssString={cssString}
        title="Bedrooms"
        hideClose={anchored}
        // Pointer/Escape dismiss only — chained hand-offs must survive the
        // closing dialog's focus return (#923; see DateFilter).
        onFocusOutside={(e) => e.preventDefault()}
      >
        {/* Anchored popovers are headerless like the toolkit's. */}
        {!anchored && (
          <div className="hk-modal-header">
            <span className="hk-h4">Bedrooms</span>
          </div>
        )}

        <div className="hk-modal-body hk-flex-1">
          <div className="hk-flex hk-flex-middle">
            <div className="hk-width-3-5">
              <label className="hk-form-label">Bedrooms</label>
              <div className="hk-text-meta">Minimum bedrooms</div>
            </div>
            <div className="hk-width-2-5">
              <div className="hk-form-controls">
                <NumberControl name="bedrooms" value={bedrooms} min={0} onChange={changeBedrooms} label="Bedrooms" />
              </div>
            </div>
          </div>
        </div>

        <div className="hk-modal-footer hk-flex">
          {bedrooms > 0 && (
            <button type="button" className="hk-button hk-button-link hfse-modal-reset-button" onClick={handleClear}>
              Clear
            </button>
          )}
          <button
            type="button"
            className="hk-button hk-button-small bg-accent text-accent-foreground hk-margin-auto-left hfse-modal-next-button"
            onClick={handleApply}
            style={{ marginLeft: 'auto' }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
